import type { Instant, LocalDate } from './index';

export class DateContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DateContractError';
  }
}

export type LocalDateTime = {
  date: LocalDate;
  hour: number;
  minute: number;
  second: number;
};

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timezone: string): Intl.DateTimeFormat {
  const existing = formatterCache.get(timezone);
  if (existing) return existing;
  let created: Intl.DateTimeFormat;
  try {
    created = new Intl.DateTimeFormat('en-US-u-ca-iso8601-nu-latn', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
  } catch {
    throw new DateContractError(`Unsupported IANA timezone: ${timezone}`);
  }
  formatterCache.set(timezone, created);
  return created;
}

function dateFromInstant(value: Instant): Date {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.valueOf())) throw new DateContractError('Invalid instant');
  return parsed;
}

function dateParts(value: LocalDate): { year: number; month: number; day: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) throw new DateContractError(`Invalid local date: ${value}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const candidate = new Date(Date.UTC(year, month - 1, day));
  if (
    candidate.getUTCFullYear() !== year ||
    candidate.getUTCMonth() !== month - 1 ||
    candidate.getUTCDate() !== day
  ) {
    throw new DateContractError(`Invalid local date: ${value}`);
  }
  return { year, month, day };
}

function asLocalDate(year: number, month: number, day: number): LocalDate {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` as LocalDate;
}

export function localDateTimeAt(value: Instant, timezone: string): LocalDateTime {
  const parts = formatter(timezone).formatToParts(dateFromInstant(value));
  const fields = new Map(parts.map((part) => [part.type, part.value]));
  const year = Number(fields.get('year'));
  const month = Number(fields.get('month'));
  const day = Number(fields.get('day'));
  const hour = Number(fields.get('hour'));
  const minute = Number(fields.get('minute'));
  const second = Number(fields.get('second'));
  if ([year, month, day, hour, minute, second].some((part) => !Number.isFinite(part))) {
    throw new DateContractError('Could not derive local date parts');
  }
  return { date: asLocalDate(year, month, day), hour, minute, second };
}

export function localDateAt(value: Instant, timezone: string): LocalDate {
  return localDateTimeAt(value, timezone).date;
}

export function addCalendarDays(value: LocalDate, amount: number): LocalDate {
  if (!Number.isInteger(amount)) throw new DateContractError('Calendar day amount must be integer');
  const { year, month, day } = dateParts(value);
  const changed = new Date(Date.UTC(year, month - 1, day + amount));
  return asLocalDate(changed.getUTCFullYear(), changed.getUTCMonth() + 1, changed.getUTCDate());
}

export function calendarDayDifference(from: LocalDate, to: LocalDate): number {
  const start = dateParts(from);
  const end = dateParts(to);
  return Math.round(
    (Date.UTC(end.year, end.month - 1, end.day) -
      Date.UTC(start.year, start.month - 1, start.day)) /
      86_400_000,
  );
}

export function studyDateAt(value: Instant, timezone: string, dayCutoffHour: number): LocalDate {
  if (!Number.isInteger(dayCutoffHour) || dayCutoffHour < 0 || dayCutoffHour > 23) {
    throw new DateContractError('dayCutoffHour must be an integer from 0 through 23');
  }
  const local = localDateTimeAt(value, timezone);
  return local.hour < dayCutoffHour ? addCalendarDays(local.date, -1) : local.date;
}

export type DDay = {
  days: number;
  label: string;
  phase: 'upcoming' | 'today' | 'past';
};

export function calculateDDay(today: LocalDate, target: LocalDate): DDay {
  const days = calendarDayDifference(today, target);
  if (days > 0) return { days, label: `D-${days}`, phase: 'upcoming' };
  if (days < 0) return { days, label: `D+${Math.abs(days)}`, phase: 'past' };
  return { days: 0, label: '시험 당일', phase: 'today' };
}

export function calculateDDayAt(value: Instant, timezone: string, target: LocalDate): DDay;
export function calculateDDayAt(
  value: Instant,
  timezone: string,
  target: LocalDate | null | undefined,
): DDay | { days: null; label: string; phase: 'unknown' };
export function calculateDDayAt(
  value: Instant,
  timezone: string,
  target: LocalDate | null | undefined,
): DDay | { days: null; label: string; phase: 'unknown' } {
  return target
    ? calculateDDay(localDateAt(value, timezone), target)
    : { days: null, label: '시험일 미정', phase: 'unknown' };
}
